import { FC, useEffect } from 'react'
import { Alert } from '@mui/material'

import { useAnyCompassEmbed } from '../contexts/EmbedContext'
import { usePatientMismatch } from '../hooks/usePatientMismatch'

// 埋め込みの突合ガード (log/193 work-log/6 §5)。
//
// 埋め込みでは PatientInfoBar が患者名を出さない (ホスト側と二重になるため) ので、
// 別患者の薬歴が表示されても薬剤師に照合手段が無い。ここで「起動 URL の患者 (SSoT)」と
// 「開いている薬歴の患者名」「INIT で親から届いた患者名」を突き合わせ、
// **不一致のときだけ** 赤帯を出す。一致しているときは何も描画しない
// (latest の設計言語は引き算。照合手段の回復だけを足し、平常時の画面は変えない)。
//
// 検出できないもの: 同姓同名・包含関係の別患者 (helpers/patientMismatch.ts の既知制約。
// 既知制約)、転記が親に破棄されたケース (ACK が要る)。
interface PatientMismatchBannerProps {
  // 開いている薬歴の患者名 (event.patient_name)。event 未取得なら null/undefined のまま
  // 渡してよい (判定不能として黙る)。
  patientName: string | null | undefined
}

export const PatientMismatchBanner: FC<PatientMismatchBannerProps> = ({
  patientName,
}) => {
  const { deeplinkPatientName, patientContext } = useAnyCompassEmbed()
  const initKnjName = patientContext?.knjName ?? null

  // 判定本体は hook に切り出し済み (転記の抑止と同じ判定を共有する)。
  const mismatch = usePatientMismatch(patientName)

  useEffect(() => {
    if (!mismatch) return
    // 黙殺しない (サポート調査でどちらの不一致かを特定する唯一の痕跡)。
    // deps は knjName の**値**で取る。patientContext は INIT 再送のたび新参照になるため、
    // オブジェクトを入れると同一内容の再送ごとに同じ warn が重複し、実イベントの数が
    // 読めなくなる。
    console.warn('[anycompass-embed] patient mismatch', {
      deeplinkPatientName,
      recordPatientName: patientName ?? null,
      initKnjName,
    })
  }, [mismatch, deeplinkPatientName, patientName, initKnjName])

  if (!mismatch) return null

  return (
    <Alert severity="error" sx={{ mb: 2 }}>
      AnyCOMPASS
      で開いている患者と、この画面の患者が一致していません。AnyCOMPASS
      から患者を開き直してください。
    </Alert>
  )
}
